import { AfterViewInit, Component, ElementRef, forwardRef, inject, Input, OnChanges, OnDestroy, OnInit } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Subscription } from 'rxjs';
import { GainsFormItemComponent } from '../gains-form-item/gains-form-item.component';

@Component({
  selector: 'gains-date-picker',
  standalone: false,
  templateUrl: './gains-date-picker.component.html',
  styleUrl: './gains-date-picker.component.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => GainsDatePickerComponent),
      multi: true
    }
  ]
})
export class GainsDatePickerComponent extends GainsFormItemComponent implements ControlValueAccessor, OnInit, OnChanges, AfterViewInit, OnDestroy {
  private static idCounter = 0;
  @Input() format = 'MM/dd/YYYY'; // Format for the date display
  @Input() allowClear = true;
  /** Picks the time too, such as for `yyyy-MM-dd HH:mm:ss`. */
  @Input() showTime = false;
  /** The id of its input, for a `<label for>`: unique per picker unless given. */
  @Input() inputId = `gains-date-picker-${GainsDatePickerComponent.idCounter++}`;
  /** Names the picker for screen readers when it has no label. */
  @Input() ariaLabel?: string;

  private readonly _element: HTMLElement = inject(ElementRef).nativeElement;
  private _changes?: Subscription;
  private _onTouched: () => void = () => undefined;

  constructor() {
    super();
  }

  ngOnInit(): void {
    this.setControl();
  }

  ngOnChanges(): void {
    this.labelInput();
  }

  ngAfterViewInit(): void {
    this.labelInput();
  }

  ngOnDestroy(): void {
    this._changes?.unsubscribe();
  }

  onPanelChanged(event: any) {
    if (event === false) {
      this.internalFormControl.markAsTouched();
      this.checkError();
      this._onTouched();
    }
  }

  onValueChange(selectedValue: Date | null) {
    this.checkError();
  }

  registerOnTouched(fn: () => void): void {
    this._onTouched = fn;
  }
  registerOnChange(fn: any): void {
    this._changes?.unsubscribe();
    this._changes = this.internalFormControl?.valueChanges.subscribe(fn);
  }
  /** Disabling is not a change of the value: the form is not told of one (it would take the picker's value for its own). */
  setDisabledState(isDisabled: boolean): void {
    if (!this.internalFormControl) return;
    if (isDisabled) {
      this.internalFormControl.disable({ emitEvent: false });
    } else {
      this.internalFormControl.enable({ emitEvent: false });
    }
  }

  /** The label for screen readers goes on the input the picker focuses, not on its wrapper. */
  private labelInput(): void {
    const input = this._element.querySelector('input');
    if (!input) return;
    if (this.ariaLabel) {
      input.setAttribute('aria-label', this.ariaLabel);
    } else {
      input.removeAttribute('aria-label');
    }
  }
}
